import * as React from 'react';
import {
  DayPicker as BaseDayPicker,
  type DayContentProps,
  type Matcher,
} from 'react-day-picker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import {View} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {DatePickerContext} from './DatePickerContext';
import {useGoToDate, offsetDateForUTC} from './__DatePicker.utils';
import {DatePickerCaption} from './DatePickerCaption';

dayjs.extend(utc);
dayjs.extend(tz);

const SingleDatePickerWrapper = emotionStyled(View)({
  padding: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

type SingleDatePickerProps = {
  value: Date;
  onChange: (date: Date) => void;
  disabled?: Matcher;
  /**
   * When provided, datepicker will display and return dates in the provided timezone. Defaults to UTC otherwise.
   */
  timezone?: string;
  CustomDayContent?: (props: DayContentProps) => JSX.Element;
};

/**
 * A simplified date picker component that allows the user to select a single date.
 * Unlike DatePicker, this component does not have a Menu wrapper or trigger button.
 */
export const SingleDatePicker = ({
  value,
  onChange,
  disabled,
  timezone,
  CustomDayContent,
}: SingleDatePickerProps) => {
  const {goToDate, setGoToDate} = useGoToDate();

  const handleOnChange = (selectedDay: Date) => {
    const finalDate = timezone
      ? dayjs(selectedDay).tz(timezone, true).startOf('day').toDate()
      : dayjs.utc(selectedDay).startOf('day').toDate();

    onChange(finalDate);
  };

  const selectedValue = offsetDateForUTC(value, timezone);

  const datePickerMarkup = (
    <BaseDayPicker
      components={{Caption: DatePickerCaption, DayContent: CustomDayContent}}
      defaultMonth={selectedValue}
      mode="single"
      onSelect={(selectedDay) => {
        if (!selectedDay) {
          return;
        }
        handleOnChange(selectedDay);
      }}
      selected={selectedValue}
      showOutsideDays
      weekStartsOn={0}
      disabled={disabled || (() => false)}
    />
  );

  return (
    <SingleDatePickerWrapper>
      <DatePickerContext.Provider value={{goToDate, setGoToDate}}>
        {datePickerMarkup}
      </DatePickerContext.Provider>
    </SingleDatePickerWrapper>
  );
};
